import React, { useRef, useState, useMemo, useEffect } from 'react';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Intl } from '@erecruit/core';
import { NamedObject, Resources, Mutations } from '@erecruit/types';
import { Form, styled, H1, H2, Flex } from '@erecruit/ui-kit';
import moment, { Moment } from 'moment';
import { RangeModifier } from '@erecruit/ui-kit/src/molecules/Calendar/Calendar';

import RoutedWizard, { WizardStep } from '../../App/components/RoutedWizard';
import { AddShiftsStep } from './components/SelectShiftsStep';
import { ReviewStep } from './components/ReviewStep';
import { selectors, addShifts } from './state/reducer';
import {
  hasValidationErrors,
  generateFieldObjects,
} from '../AddNewPositionWizard/helpers';
import { AddNewShiftWizardFields, WizardContext } from './types';
import { getKey } from './components/SelectShiftsStep/utilities';

const { FormattedMessage, injectIntl } = Intl;

interface AddNewShiftWizardProps {
  selectedPositionId: number;
  selectedPosition: Resources.PositionSearch | null;
  isLoading: boolean;
  positionDepartments: NamedObject<number>[];
  positionShiftTypes: Resources.ShiftType[];
  addShifts: typeof addShifts;
  shiftsCreated: boolean;
}

function isformValid(form?: WrappedFormUtils) {
  form && form.validateFields();
  return !hasValidationErrors(form && form.getFieldsError());
}

export interface AddNewShiftInitialData {
  departments: NamedObject<number>[];
  shiftTypes: Resources.ShiftType[];
  selectedPosition: Resources.PositionSearch | null;
}

function getWizardSteps(
  formData: AddNewShiftInitialData,
  pushPrevious: () => void,
  intl: Intl.InjectedIntl,
): WizardStep[] {
  return [
    {
      title: intl.formatMessage({
        id: messageIds.vms.newShiftWizard.general.selectShifts,
      }),
      name: 'selectShifts',
      route: '/addNewShiftWizard/selectShifts',
      forwardLabel: intl.formatMessage({
        id: messageIds.vms.newShiftWizard.general.review,
      }),
      width: 893,
      content: () => <AddShiftsStep formData={formData} />,
    },
    {
      title: intl.formatMessage({
        id: messageIds.vms.newShiftWizard.general.review,
      }),
      name: 'review',
      route: '/addNewShiftWizard/review',
      forwardLabel: intl.formatMessage({
        id: messageIds.vms.newShiftWizard.general.submit,
      }),
      width: 624,
      content: () => (
        <ReviewStep pushPrevious={pushPrevious} formData={formData} />
      ),
    },
  ];
}

const Title = styled(H1)({
  marginBottom: 0,
});

const Subtitle = styled(H2)({
  opacity: 0.4,
});

const getTime = (value?: moment.Moment) => {
  value = value || moment();
  return {
    hours: value.get('hours'),
    minutes: value.get('minutes'),
  };
};

const calculateStartAndFinish = (
  startDate: Moment,
  startTime: { hours: number; minutes: number },
  endTime: { hours: number; minutes: number },
  quantity = 1,
): Mutations.ShiftRequest[] => {
  const start = moment(startDate);
  start.hours(startTime.hours);
  start.minutes(startTime.minutes);

  const end = moment(startDate);
  end.hours(endTime.hours);
  end.minutes(endTime.minutes);

  if (endTime.hours <= startTime.hours) {
    end.add(1, 'days');
  }

  return [...Array(quantity)].map(() => ({
    start: start.toDate(),
    end: end.toDate(),
  }));
};

const AddNewShiftWizard = injectIntl(
  ({
    selectedPosition,
    positionShiftTypes,
    positionDepartments,
    isLoading,
    addShifts,
    shiftsCreated,
    intl,
  }: AddNewShiftWizardProps & Intl.InjectedIntlProps) => {
    const formRef = useRef<WrappedFormUtils>();
    const form = formRef.current;

    const [showWizard, setShowWizard] = useState(true);

    const formData: AddNewShiftInitialData = useMemo(
      () => ({
        departments: positionDepartments,
        shiftTypes: positionShiftTypes,
        selectedPosition: selectedPosition,
      }),
      [positionDepartments, positionShiftTypes, selectedPosition],
    );

    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
      if (shiftsCreated) {
        history.push('/shifts');
      }
    }, [shiftsCreated, history]);

    const pushPrevious = () => history.goBack();

    const wizardSteps = getWizardSteps(formData, pushPrevious, intl);

    const [formFieldsValues, setFormFieldValues] = useState<
      AddNewShiftWizardFields
    >({
      selectedDates: {},
      selectedDatesQuantities: {},
      selectionPerType: { multiple: [], range: [], single: [] },
    });

    const progress = wizardSteps.findIndex(
      step => step.route === location.pathname,
    );

    useEffect(() => {
      if (progress === 0) {
        const fields = generateFieldObjects(formFieldsValues);
        form && form.setFields(fields);
      }
    }, [form, formFieldsValues, progress]);

    const handleBack = (pushPreviousStep: Function) => {
      if (progress === 0) {
        history.push('/addNewShift');
      } else {
        const formValues = (form?.getFieldsValue() as AddNewShiftWizardFields) || {
          selectedDates: {},
          selectedDatesQuantities: {},
          selectionPerType: { multiple: [], range: [], single: [] },
        };
        setFormFieldValues({
          ...formFieldsValues,
          selectionPerType: formValues.selectionPerType,
          selectedDatesQuantities: formValues.selectedDatesQuantities,
          selectedDates: formValues.selectedDates,
        });
        pushPreviousStep();
      }
    };

    const handleNextStep = (nextStepCb: Function) => {
      if (!isformValid(form)) {
        return;
      }

      const formValues = (form?.getFieldsValue() as AddNewShiftWizardFields) || {
        selectedDates: {},
        selectedDatesQuantities: {},
        selectionPerType: { multiple: [], range: [], single: [] },
      };

      setFormFieldValues(formValues);
      nextStepCb();
    };

    const handleSubmit = () => {
      if (!isformValid(form)) {
        return;
      }

      const formFields = form?.getFieldsValue() as AddNewShiftWizardFields;
      const selectedDates = formFields.selectedDates;
      const selectedDatesQuantities = formFields.selectedDatesQuantities;

      const startTime = getTime(formFieldsValues.startTime);
      const endTime = getTime(formFieldsValues.endTime);
      const shiftsRange: Mutations.ShiftRequest[] = [];

      Object.keys(selectedDates)
        .map(key => selectedDates[key])
        .forEach(date => {
          const key = getKey(date);
          if (Object.keys(date as object).length === 0) {
            calculateStartAndFinish(
              moment(date as Date),
              startTime,
              endTime,
              selectedDatesQuantities[key],
            ).forEach(shiftRange => shiftsRange.push(shiftRange));
          } else {
            const key = getKey(date);
            const quantity = selectedDatesQuantities[key];
            const startOfRange = moment((date as RangeModifier).from);
            const endOfRange = moment((date as RangeModifier).to);

            const days = endOfRange.diff(startOfRange, 'days');

            for (let i = 0; i < days + 1; i++) {
              const currentDay = moment(startOfRange);
              currentDay.add(i, 'days');
              calculateStartAndFinish(
                currentDay,
                startTime,
                endTime,
                quantity,
              ).forEach(shiftRange => shiftsRange.push(shiftRange));
            }
          }
        });

      const params = {
        facilityDepartmentId: Number(formFieldsValues.departmentId),
        shiftTypeId: Number(formFieldsValues.shiftTypeId),
        positionId: Number(selectedPosition?.id),
        shifts: shiftsRange,
        positionName: String(selectedPosition?.name),
      };

      setShowWizard(false);
      addShifts(params);
    };

    return (
      <Form wrappedComponentRef={formRef}>
        <WizardContext.Provider value={formFieldsValues}>
          {showWizard && (
            <RoutedWizard
              header={
                <Flex direction="column" align="center">
                  <Title color="headerTertiary">
                    <FormattedMessage
                      id={messageIds.vms.newShiftWizard.title}
                    />
                  </Title>
                  <Subtitle color="headerTertiary">
                    {selectedPosition?.name}
                  </Subtitle>
                </Flex>
              }
              showProgress={false}
              width={wizardSteps[progress]?.width}
              isLoading={isLoading}
              steps={wizardSteps}
              onBack={handleBack}
              onNextStep={handleNextStep}
              onSubmit={handleSubmit}
            />
          )}
        </WizardContext.Provider>
      </Form>
    );
  },
);

export default connect(selectors.selectAddNewShiftState, { addShifts })(
  AddNewShiftWizard,
);
